import type { AudioCarrierPlan } from '@spectrabus/audio-modem';
import { base64UrlEncode, type ReceiverLiveSession, type SenderLiveSession } from '@spectrabus/live-engine';
import { OPTICAL_FRAME_HEIGHT, OPTICAL_FRAME_WIDTH } from '@spectrabus/optical-encoder';
import { bytesToHex, type IntegrityState } from '@spectrabus/protocol';
import {
  metric,
  type CarrierTelemetry,
  type IntegrityCheck,
  type LinkSnapshot,
  type Metric,
} from '@spectrabus/telemetry';

/**
 * Builders that project the live engines onto the LinkSnapshot contract the
 * transfer console renders. Every field is provenance-honest: values the
 * local peer genuinely measured are 'measured', values reproduced from
 * protocol state are 'derived', and everything this peer cannot know is
 * 'unavailable' rather than invented.
 */

export interface LiveRates {
  readonly opticalBytesPerSecond: number;
  readonly acousticBytesPerSecond: number;
  readonly decoderLatencyMs: number | null;
  readonly cameraFps: number | null;
  readonly displayCadenceHz: number | null;
  readonly returnBurstFailures?: number | null;
  readonly returnBurstsPlayed?: number | null;
}

function unavailable(unit: string, nowMs: number): Metric<number> {
  return metric(0, unit, 'unavailable', nowMs, 0);
}

function carrierTelemetry(plan: AudioCarrierPlan): readonly CarrierTelemetry[] {
  return plan.carriers.map((carrier) => ({
    index: carrier.index,
    frequencyHz: carrier.frequencyHz,
    snrDb: carrier.snrDb,
    modulation: carrier.modulation,
    enabled: carrier.enabled,
  }));
}

function integrityCheck(
  stage: IntegrityCheck['stage'],
  label: string,
  state: IntegrityState,
  detail: string,
): IntegrityCheck {
  return { stage, label, state, detail };
}

const EMPTY_CELLS = new Uint8Array(OPTICAL_FRAME_WIDTH * OPTICAL_FRAME_HEIGHT);

export function buildSenderLiveSnapshot(
  engine: SenderLiveSession,
  forwardPlan: AudioCarrierPlan,
  rates: LiveRates,
  nowMs = Date.now(),
): LinkSnapshot {
  const session = engine.session;
  const generations = engine.generationSummaries;
  const requiredIndependentSymbols = generations.reduce((total, generation) => total + generation.sourceCount, 0);
  const knownRank = generations.reduce(
    (total, generation) =>
      total +
      (Number.isFinite(generation.reportedDeficit)
        ? Math.max(0, generation.sourceCount - generation.reportedDeficit)
        : 0),
    0,
  );
  const verifiedBytes = engine.receiverObjectHash !== null ? engine.objectByteLength : 0;
  const feedback = engine.latestFeedback;
  const verifiedConfirmations =
    engine.verifiedReturnChannels.length + (engine.receiverAuthorised ? 2 : 0);
  const elapsed = engine.elapsedMs;
  const frame = engine.lastOpticalFrame;
  const complete = engine.phase === 'COMPLETE';

  return {
    provenance: {
      channel: 'measured',
      raster: 'measured',
      carrierPlan: 'derived',
      spatialQuality: 'unavailable',
      scheduler: 'measured',
      admissions: 'derived',
      handshake: 'measured',
      cryptography: 'derived',
      hashes: 'derived',
    },
    phase: engine.phase,
    phaseDetail:
      engine.phase === 'DISCOVER' || engine.phase === 'KEY_CONFIRM'
        ? `${engine.phaseDetail} RETURN PATH (AUDIO + QR): ${engine.ledger.returnPayloadsAccepted} ACCEPTED · ${engine.ledger.returnPayloadsRejected} PROTOCOL-REJECTED · ${rates.returnBurstFailures ?? 0} PHY-REJECTED.`
        : engine.phaseDetail,
    handshake: {
      protocol: 'SBHS/1',
      keyAgreement: 'P-256 / HKDF-SHA-256',
      transcriptHash: session?.transcriptHash ?? '',
      transferId: engine.transferId,
      discoveryCommitment: engine.discoveryCommitment,
      encodedOffer: base64UrlEncode(engine.offerBytes),
      offerBytes: 141,
      responseBytes: session === null ? 0 : 141,
      offerResponseBound: session !== null,
      confirmationBytes: 96,
      verifiedConfirmations,
      requiredConfirmations: 4,
      mutuallyConfirmedChannels: engine.receiverAuthorised ? engine.verifiedReturnChannels : [],
      transferAuthorised:
        engine.phase === 'TRANSFER' ||
        engine.phase === 'REPAIR' ||
        engine.phase === 'VERIFY' ||
        complete,
      source: 'measured',
      transportSource: 'measured',
    },
    sessionId: engine.sessionId,
    sessionFingerprint: session?.keyFingerprint ?? '',
    sessionPublicKey: bytesToHex(engine.offerBytes.slice(76)),
    manifestCommitment: engine.sealed?.manifestCommitment ?? '',
    objectName: engine.objectName,
    objectMimeType: engine.objectMimeType,
    objectBytes: engine.objectByteLength,
    elapsedMs: elapsed,
    reconstructedFraction:
      requiredIndependentSymbols === 0 ? 0 : knownRank / requiredIndependentSymbols,
    verifiedBytes,
    totalIndependentSymbols: knownRank,
    requiredIndependentSymbols,
    unresolvedSymbols: Math.max(0, requiredIndependentSymbols - knownRank),
    integrityViolations: engine.integrityViolations,
    opticalProfile: 'O1-BINARY 120x80',
    acousticProfile: 'A1-FDD-FWD 2.1-8.3 kHz',
    opticalGoodput: unavailable('B/s', nowMs),
    acousticGoodput: unavailable('B/s', nowMs),
    aggregateGoodput: unavailable('B/s', nowMs),
    opticalRawBitrate: metric(
      Math.round((engine.ledger.opticalEmittedBytes * 8_000) / Math.max(1, elapsed)),
      'bit/s',
      'measured',
      nowMs,
      engine.ledger.opticalEmissions,
    ),
    acousticRawBitrate: metric(
      Math.round((engine.ledger.acousticEmittedBytes * 8_000) / Math.max(1, elapsed)),
      'bit/s',
      'measured',
      nowMs,
      engine.ledger.acousticEmissions,
    ),
    cameraFps: metric<number | null>(null, 'fps', 'unavailable', nowMs, 0),
    displayCadence: metric(
      rates.displayCadenceHz ?? 0,
      'Hz',
      rates.displayCadenceHz === null ? 'unavailable' : 'measured',
      nowMs,
    ),
    feedbackRtt: unavailable('ms', nowMs),
    decoderLatency: unavailable('ms', nowMs),
    fountainOverhead: unavailable('%', nowMs),
    duplicateRate: unavailable('%', nowMs),
    spatialUtilisation: unavailable('%', nowMs),
    environmentalStability: unavailable('%', nowMs),
    opticalAllocation:
      engine.ledger.opticalEmissions + engine.ledger.acousticEmissions === 0
        ? 0
        : engine.ledger.opticalEmissions /
          (engine.ledger.opticalEmissions + engine.ledger.acousticEmissions),
    acousticAllocation:
      engine.ledger.opticalEmissions + engine.ledger.acousticEmissions === 0
        ? 0
        : engine.ledger.acousticEmissions /
          (engine.ledger.opticalEmissions + engine.ledger.acousticEmissions),
    generations: generations.map((generation) => ({
      generationId: generation.generationId,
      rank: Number.isFinite(generation.reportedDeficit)
        ? Math.max(0, generation.sourceCount - generation.reportedDeficit)
        : 0,
      requiredRank: generation.sourceCount,
      opticalContributions: 0,
      acousticContributions: 0,
      verified: feedback?.objectVerified === true,
    })),
    carriers: carrierTelemetry(forwardPlan),
    spatialRegions: [],
    integrityChecks: [
      integrityCheck(
        'physical-crc',
        'Physical frame integrity',
        engine.phase === 'DISCOVER' ? 'pending' : complete ? 'verified' : 'active',
        'CRC32C and HMAC-SHA-256 frame authentication on every emission.',
      ),
      integrityCheck(
        'fountain-rank',
        'Fountain rank progress',
        feedback === null ? 'pending' : complete ? 'verified' : 'active',
        'Rank reported physically by the receiver over the return band.',
      ),
      integrityCheck(
        'chunk-hashes',
        'Chunk hash verification',
        complete ? 'verified' : feedback?.manifestReceived === true ? 'active' : 'pending',
        'Verified on the receiver against the committed manifest.',
      ),
      integrityCheck(
        'merkle-root',
        'Merkle root',
        complete ? 'verified' : 'pending',
        'Receiver-side reconstruction over verified ciphertext.',
      ),
      integrityCheck(
        'aead-tags',
        'AES-GCM chunk tags',
        complete ? 'verified' : 'pending',
        'Authenticated decryption happens only on the receiver.',
      ),
      integrityCheck(
        'object-hash',
        'Final object hash',
        engine.phase === 'FAILED'
          ? 'failed'
          : complete
            ? 'verified'
            : 'pending',
        complete
          ? 'Receiver returned the matching SHA-256 over the return band.'
          : 'Awaiting the physical receiver verification report.',
      ),
    ],
    incidents: [],
    counters: {
      optical: {
        accepted: engine.ledger.opticalEmissions,
        rejected: 0,
        duplicate: 0,
        independent: 0,
      },
      acoustic: {
        accepted: engine.ledger.acousticEmissions,
        rejected: 0,
        duplicate: 0,
        independent: 0,
      },
    },
    currentFrameCells: frame.cells.slice(),
    currentFrameWidth: frame.width,
    currentFrameHeight: frame.height,
    objectHash: engine.senderObjectHash,
    senderHash: engine.senderObjectHash,
    downloadBytes: null,
    failure: engine.failure,
  };
}

export function buildReceiverLiveSnapshot(
  engine: ReceiverLiveSession,
  forwardPlan: AudioCarrierPlan,
  rates: LiveRates,
  nowMs = Date.now(),
): LinkSnapshot {
  const generations = engine.generationSummaries;
  const manifest = engine.manifest;
  const requiredIndependentSymbols =
    manifest === null
      ? generations.reduce((total, generation) => total + generation.requiredRank, 0)
      : manifest.chunks.reduce((total, entry) => total + Math.ceil(entry.ciphertextLength / 512), 0);
  const totalIndependentSymbols = generations.reduce((total, generation) => total + generation.rank, 0);
  const verifiedBytes = generations.reduce((total, generation, index) => {
    if (!generation.verified) return total;
    const entry = manifest?.chunks[index];
    return total + (entry?.plaintextLength ?? 0);
  }, 0);
  const elapsed = engine.elapsedMs;
  const complete = engine.phase === 'COMPLETE';
  const frame = engine.lastOpticalFrame;
  const admitted = engine.ledger.admittedEquations;
  const ledger = engine.ledger;

  return {
    provenance: {
      channel: 'measured',
      raster: 'derived',
      carrierPlan: 'derived',
      spatialQuality: 'unavailable',
      scheduler: 'measured',
      admissions: 'measured',
      handshake: 'measured',
      cryptography: 'derived',
      hashes: 'derived',
    },
    phase: engine.phase,
    phaseDetail:
      engine.phase === 'DISCOVER' || engine.phase === 'CALIBRATE' || engine.phase === 'KEY_CONFIRM'
        ? `${engine.phaseDetail} RETURN BURSTS PLAYED: ${rates.returnBurstsPlayed ?? 0}.`
        : engine.phaseDetail,
    handshake: {
      protocol: 'SBHS/1',
      keyAgreement: 'P-256 / HKDF-SHA-256',
      transcriptHash: engine.session.transcriptHash,
      transferId: engine.session.transferId,
      discoveryCommitment: engine.discoveryCommitment,
      encodedOffer: '',
      offerBytes: 141,
      responseBytes: 141,
      offerResponseBound: true,
      confirmationBytes: 96,
      verifiedConfirmations: engine.verifiedForwardChannels.length + (engine.transferAuthorised ? 2 : 0),
      requiredConfirmations: 4,
      mutuallyConfirmedChannels: engine.transferAuthorised ? engine.verifiedForwardChannels : [],
      transferAuthorised: engine.transferAuthorised,
      source: 'measured',
      transportSource: 'measured',
    },
    sessionId: engine.session.sessionId,
    sessionFingerprint: engine.session.keyFingerprint,
    sessionPublicKey: bytesToHex(engine.session.receiverPublicKey),
    manifestCommitment: engine.manifestCommitment ?? '',
    objectName: manifest?.name ?? 'AWAITING MANIFEST',
    objectMimeType: manifest?.mimeType ?? 'application/octet-stream',
    objectBytes: manifest?.byteLength ?? 0,
    elapsedMs: elapsed,
    reconstructedFraction:
      requiredIndependentSymbols === 0 ? 0 : totalIndependentSymbols / requiredIndependentSymbols,
    verifiedBytes,
    totalIndependentSymbols,
    requiredIndependentSymbols,
    unresolvedSymbols: Math.max(0, requiredIndependentSymbols - totalIndependentSymbols),
    integrityViolations: engine.integrityViolations,
    opticalProfile: 'O1-BINARY 120x80',
    acousticProfile: 'A1-FDD-FWD 2.1-8.3 kHz',
    opticalGoodput: metric(
      Math.round((verifiedBytes * 1_000) / Math.max(1, elapsed)),
      'B/s',
      'measured',
      nowMs,
      ledger.channels.optical.independent,
    ),
    acousticGoodput: metric(
      Math.round((ledger.channels.acoustic.independent * 512 * 1_000) / Math.max(1, elapsed)),
      'B/s',
      'measured',
      nowMs,
      ledger.channels.acoustic.independent,
    ),
    aggregateGoodput: metric(
      Math.round((totalIndependentSymbols * 512 * 1_000) / Math.max(1, elapsed)),
      'B/s',
      'measured',
      nowMs,
      totalIndependentSymbols,
    ),
    opticalRawBitrate: metric(
      Math.round(rates.opticalBytesPerSecond * 8),
      'bit/s',
      'measured',
      nowMs,
      ledger.channels.optical.accepted,
    ),
    acousticRawBitrate: metric(
      Math.round(rates.acousticBytesPerSecond * 8),
      'bit/s',
      'measured',
      nowMs,
      ledger.channels.acoustic.accepted,
    ),
    cameraFps: metric<number | null>(
      rates.cameraFps,
      'fps',
      rates.cameraFps === null ? 'unavailable' : 'measured',
      nowMs,
    ),
    displayCadence: unavailable('Hz', nowMs),
    feedbackRtt: unavailable('ms', nowMs),
    decoderLatency: metric(
      rates.decoderLatencyMs ?? 0,
      'ms',
      rates.decoderLatencyMs === null ? 'unavailable' : 'measured',
      nowMs,
    ),
    fountainOverhead: metric(
      admitted === 0 ? 0 : Math.round(((admitted - totalIndependentSymbols) / admitted) * 100),
      '%',
      'measured',
      nowMs,
      admitted,
    ),
    duplicateRate: metric(
      admitted === 0 ? 0 : Math.round((ledger.duplicates / admitted) * 100),
      '%',
      'measured',
      nowMs,
      admitted,
    ),
    spatialUtilisation: unavailable('%', nowMs),
    environmentalStability: unavailable('%', nowMs),
    opticalAllocation:
      ledger.channels.optical.accepted + ledger.channels.acoustic.accepted === 0
        ? 0
        : ledger.channels.optical.accepted /
          (ledger.channels.optical.accepted + ledger.channels.acoustic.accepted),
    acousticAllocation:
      ledger.channels.optical.accepted + ledger.channels.acoustic.accepted === 0
        ? 0
        : ledger.channels.acoustic.accepted /
          (ledger.channels.optical.accepted + ledger.channels.acoustic.accepted),
    generations: generations.map((generation) => ({
      generationId: generation.generationId,
      rank: generation.rank,
      requiredRank: generation.requiredRank,
      opticalContributions: generation.opticalContributions,
      acousticContributions: generation.acousticContributions,
      verified: generation.verified,
    })),
    carriers: carrierTelemetry(forwardPlan),
    spatialRegions: [],
    integrityChecks: [
      integrityCheck(
        'physical-crc',
        'Physical frame integrity',
        ledger.forwardPayloadsAccepted > 0 ? (complete ? 'verified' : 'active') : 'pending',
        `${ledger.forwardPayloadsAccepted} authenticated frames accepted, ${ledger.forwardPayloadsRejected} rejected.`,
      ),
      integrityCheck(
        'fountain-rank',
        'Fountain rank progress',
        complete
          ? 'verified'
          : totalIndependentSymbols > 0
            ? 'active'
            : 'pending',
        `${totalIndependentSymbols}/${requiredIndependentSymbols} independent equations admitted.`,
      ),
      integrityCheck(
        'chunk-hashes',
        'Chunk hash verification',
        complete
          ? 'verified'
          : generations.some((generation) => generation.verified)
            ? 'active'
            : 'pending',
        'SHA-256 per reconstructed ciphertext chunk against the manifest.',
      ),
      integrityCheck(
        'merkle-root',
        'Merkle root',
        complete ? 'verified' : 'pending',
        'Recomputed over every verified chunk at object open.',
      ),
      integrityCheck(
        'aead-tags',
        'AES-GCM chunk tags',
        complete ? 'verified' : generations.some((generation) => generation.verified) ? 'active' : 'pending',
        'Authenticated decryption with the handshake-derived object key.',
      ),
      integrityCheck(
        'object-hash',
        'Final object hash',
        engine.phase === 'FAILED' ? 'failed' : complete ? 'verified' : 'pending',
        complete
          ? 'Recovered object SHA-256 matches the committed manifest.'
          : 'Verified after every generation opens.',
      ),
    ],
    incidents: [],
    counters: {
      optical: { ...ledger.channels.optical },
      acoustic: { ...ledger.channels.acoustic },
    },
    currentFrameCells: frame === null ? EMPTY_CELLS.slice() : frame.cells.slice(),
    currentFrameWidth: frame?.width ?? OPTICAL_FRAME_WIDTH,
    currentFrameHeight: frame?.height ?? OPTICAL_FRAME_HEIGHT,
    objectHash: engine.result?.objectHash ?? null,
    senderHash: manifest?.objectHash ?? null,
    downloadBytes: engine.result?.bytes ?? null,
    failure: engine.failure,
  };
}
